import React, { useEffect, useState } from 'react';

import analytics from '@repo/analytics';
import { AUTHENTICATION_CLICK_SIGNUP } from '@repo/editor/src/global/analytics/events';
import {
  useAuthenticationStore,
  authenticationStoreSelector,
} from '@repo/editor/src/stores/authenticationStore';
import {
  designsStoreSelector,
  useDesignsStore,
} from '@repo/editor/src/stores/designsStore';
import {
  promotionSelector,
  usePromotionStore,
} from '@repo/editor/src/stores/promotionStore';
import {
  useUserStore,
  userStoreSelector,
} from '@repo/editor/src/stores/userStore';
import { State as UserState } from '@repo/editor/src/stores/userStore/types';
import { withEventScope } from '@repo/event-processor';
import {
  Button,
  primaryTheme,
  secondaryTheme,
} from '@repo/ui/components/Buttons';
import BookmarkIcon from '@repo/ui/components/Icon/BookmarkIcon';
import CopyIcon from '@repo/ui/components/Icon/CopyIcon';
import FilledBookmarkIcon from '@repo/ui/components/Icon/FilledBookmarkIcon';
import FilledHeartIcon from '@repo/ui/components/Icon/FilledHeartIcon';
import HeartIcon from '@repo/ui/components/Icon/HeartIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { fireToast } from '@repo/ui/components/Toast';

import VerifiedModal from 'components/Challenges/VerifiedModal/VerifiedModal';
import usePostUpdate from 'components/hooks/usePostUpdate';
import useUpvotes from 'components/hooks/useUpvotes';
import LinkButton from 'components/LinkButton/LinkButton';
import ResponsiveSpacer from 'components/ResponsiveSpacer/ResponsiveSpacer';
import {
  buildEditorUrlWithTemplateParams,
  getTemplateSEOName,
} from 'global/urlUtils';
import useWindowSize from 'global/utils/useWindowSize';
import {
  useMobileRoutingStore,
  mobileRoutingStoreSelector,
} from 'stores/mobileRoutingStore';
import { postsStoreSelector, usePostsStore } from 'stores/postsStore';

import { LayoutVariation } from '../../styles/queries';
import CommentList from '../CommentList/CommentList';

import ActionButtons from './ActionButtons/ActionButtons';
import ChallengeInfo from './ChallengeInfo/ChallengeInfo';
import { DesignPostRemix } from './DesignPostRemix/DesignPostRemix';
import { DesignPostTags } from './DesignPostTags/DesignPostTags';
import Preview from './Preview/Preview';
import { ProfileRow } from './ProfileRow/ProfileRow';
import Share from './Share/Share';
import {
  Post,
  Information,
  Title,
  Description,
  InformationWrapper,
  Grid,
  NonGridSpacer,
  MobileEditTemplateButtonWrapper,
} from './styles';
import Tags from './Tags/Tags';
import { DesignPostProps } from './types';

const bookmarkSelector = (state: UserState) => state.bookmarks;

const DesignPost = ({ fromAuthorPage = false, ...props }: DesignPostProps) => {
  const { width, isNewsfeed } = props;

  const { isMobile, isTabletMin } = useWindowSize();
  const openAuthentication = useAuthenticationStore(
    authenticationStoreSelector.openAuthentication
  );
  const user = useUserStore(userStoreSelector.user);
  const proAccess = useUserStore(userStoreSelector.proAccess);
  const bookmarks = useUserStore(bookmarkSelector);
  const [, setComments] = useState({});

  const getPost = usePostsStore(postsStoreSelector.getPost);
  const addPost = usePostsStore(postsStoreSelector.addPost);
  const likePost = usePostsStore(postsStoreSelector.likePost);
  const canUserCreateDesign = useDesignsStore(
    designsStoreSelector.canUserCreateDesign
  );

  const [post, setPost] = useState(props.post);

  const [bookmark, setBookmark] = useState<{
    bookmarked: boolean | null;
    status: string;
  }>({
    status: 'idle',
    bookmarked: null,
  });
  const [showVerified, setShowVerified] = useState(false);
  const { handleUpvote, upvotedByUser } = useUpvotes(props.post);
  const tryRoutingMobileNavigation = useMobileRoutingStore(
    mobileRoutingStoreSelector.tryRouting
  );

  usePostUpdate(props.post.id, setPost);

  useEffect(() => {
    const storePost = getPost(props.post.id);
    if (storePost) {
      setPost(storePost);
    } else {
      addPost(props.post);
      setPost(props.post);
    }
  }, [props.post, props.post.id, getPost, addPost]);

  const onUpvote = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (!user) {
      analytics.track(AUTHENTICATION_CLICK_SIGNUP, { label: 'upvoteDesign' });
      openAuthentication();
      return;
    }

    if (!user.confirmed) {
      const updatedUser = await useUserStore.getState().updateUserInfo();
      if (!updatedUser?.confirmed) {
        setShowVerified(true);
        return;
      }
    }

    handleUpvote(event);
  };

  useEffect(() => {
    setBookmark((state: { bookmarked: boolean | null; status: string }) => ({
      ...state,
      bookmarked: bookmarks.includes(post.template.id),
    }));
  }, [bookmarks, post.template.id]);

  const onPostLike = () => {
    if (!user) {
      analytics.track(AUTHENTICATION_CLICK_SIGNUP, {
        label: 'likeDesign',
      });
      openAuthentication();
      return;
    }

    likePost(post.id);
  };

  const checkIfUserCanCreateDesign = () => {
    if (!redirectUrl) {
      return;
    }

    canUserCreateDesign({
      canShowProjectListModal: true,
      redirectUrl: redirectUrl,
      cb: () => window.open(redirectUrl, '_blank'),
    });
  };

  const onBookmark = async () => {
    if (!user) {
      analytics.track(AUTHENTICATION_CLICK_SIGNUP, {
        label: 'bookmarkDesign',
      });
      openAuthentication();
      return;
    }
    setBookmark((state) => ({ ...state, status: 'pending' }));

    const previousBookmarked = bookmark.bookmarked;

    await useUserStore
      .getState()
      .updateBookmark(Boolean(previousBookmarked), post.template.id);

    setBookmark({
      bookmarked: !previousBookmarked,
      status: 'resolved',
    });
    fireToast({
      label: `${previousBookmarked ? 'Removed from' : 'Saved to'} Bookmarks`,
    });
  };

  const showUpgradeModal = usePromotionStore(
    promotionSelector.showUpgradeModal
  );

  const redirectToEditorAfterAuth = !post.template.premium;

  const redirectUrl = redirectToEditorAfterAuth
    ? buildEditorUrlWithTemplateParams({
        id: post.template.id,
        useCase: props.currentUseCase,
        subUseCase: props.currentSubUseCase,
      })
    : undefined;

  const onUse = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) {
      analytics.track(AUTHENTICATION_CLICK_SIGNUP, {
        label: 'useDesign',
      });
      openAuthentication({
        onSubmit: () => {
          checkIfUserCanCreateDesign();
        },
      });
      return;
    }

    if (post.template.premium && !proAccess) {
      showUpgradeModal('premiumDesignUsage', {
        designPreview: post.template.templatePreviewObjectName,
        data: {
          templateId: post.template.id,
        },
      });
      return;
    }

    if (isMobile) {
      tryRoutingMobileNavigation(() => checkIfUserCanCreateDesign());
      return;
    }
    // checks if designslimit is reached and routes to editor if not.
    checkIfUserCanCreateDesign();
  };

  const likes = {
    likedByUser: post.likedByUser,
    total: post.likes,
    status: post.likeStatus,
  };
  const bookmarkLabel =
    user && bookmark.bookmarked === null
      ? 'Loading...'
      : bookmark.bookmarked
        ? 'Bookmarked'
        : 'Bookmark';

  const EditTemplateButton = () => (
    <LinkButton
      buttonAction={onUse}
      dataTestId="design-post--use-button"
      height="40px"
      href={buildEditorUrlWithTemplateParams({
        id: post.template.id,
        useCase: props.currentUseCase,
        subUseCase: props.currentSubUseCase,
      })}
      icon={{ component: CopyIcon, height: '16px' }}
      isButton={
        !user ||
        user.designsLimitReached ||
        (post.template.premium && !proAccess)
      }
      isCentered={true}
      label="Use This Design"
      target="_blank"
      theme={primaryTheme}
      width="100%"
    />
  );

  return (
    <>
      <Post data-testid="post-modal" isNewsfeed={isNewsfeed}>
        <Grid isNewsfeed={isNewsfeed}>
          {isMobile || isNewsfeed ? (
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  position: 'absolute',
                  zIndex: 1,
                  display: 'flex',
                  right: 0,
                  padding: '16px',
                }}
              >
                <Button
                  dataTestId="design-post--like-button"
                  disabled={likes.status === 'pending'}
                  icon={{
                    component: likes.likedByUser ? FilledHeartIcon : HeartIcon,
                    height: '14px',
                  }}
                  label={isTabletMin ? undefined : `${likes.total}`}
                  name="designPostLikeButton"
                  theme={secondaryTheme}
                  width="auto "
                  onClick={onPostLike}
                />
                <ResponsiveSpacer dw="16px" mw="12px" />
                <Button
                  dataTestId="design-post--bookmark-button"
                  icon={{
                    component:
                      bookmarkLabel === 'Bookmarked'
                        ? FilledBookmarkIcon
                        : BookmarkIcon,
                  }}
                  label={isTabletMin ? undefined : bookmarkLabel}
                  name="designPostBookmarkButton"
                  theme={secondaryTheme}
                  width="auto"
                  onClick={onBookmark}
                />
              </div>
              <Preview
                canClick={!user}
                isNewsfeed={isNewsfeed}
                template={post.template}
                width={width}
                onClick={onUse}
              />
            </div>
          ) : (
            <>
              <NonGridSpacer
                h={isMobile ? '16px' : '24px'}
                isNewsfeed={isNewsfeed}
              />
              <Preview
                canClick={!user}
                isNewsfeed={isNewsfeed}
                template={post.template}
                width={width}
                onClick={onUse}
              />
            </>
          )}

          <NonGridSpacer
            h={isMobile ? '16px' : '24px'}
            isNewsfeed={isNewsfeed}
          />
          <ProfileRow
            fromAuthorPage={fromAuthorPage}
            post={post}
            onClose={props.onClose}
          />

          <NonGridSpacer
            h={isMobile ? '24px' : '12px'}
            isNewsfeed={isNewsfeed}
          />

          {!isMobile && !isNewsfeed && (
            <>
              <ActionButtons
                bookmarkLabel={bookmarkLabel}
                likes={likes}
                post={post}
                onBookmark={onBookmark}
                onLike={onPostLike}
                onUse={onUse}
              />
              <NonGridSpacer
                h={isMobile ? '16px' : '24px'}
                isNewsfeed={isNewsfeed}
              />
            </>
          )}

          <Information>
            <InformationWrapper isNewsfeed={isNewsfeed}>
              <DesignPostTags post={post} />
              <Title>{getTemplateSEOName(post.template)}</Title>
              {post.template.description && (
                <Description as="h2">{post.template.description}</Description>
              )}
              <DesignPostRemix remixOfTemplate={post.template.remixOf} />

              {post.challenge && (
                <ChallengeInfo
                  post={post}
                  upvotedByUser={upvotedByUser}
                  onUpvote={onUpvote}
                />
              )}
              {(!isTabletMin || !props.stickyUseDesignButton) && (
                <>
                  <Spacer height="24px" />
                  <LayoutVariation
                    breakpoint="largeTabletAndUp"
                    desktopElement={<Share post={post} />}
                    mobileElement={<EditTemplateButton />}
                  />
                </>
              )}
              {(isMobile || isNewsfeed) && (
                <>
                  <Spacer height={isMobile || isNewsfeed ? '24px' : '48px'} />
                  <Share post={post} />
                </>
              )}
              <Spacer height={isMobile || isNewsfeed ? '24px' : '48px'} />
              <CommentList
                closeParent={props.onClose}
                isNewsfeed={isNewsfeed}
                modalRef={props.modalRef}
                post={post}
                onUpdate={setComments}
              />
            </InformationWrapper>
          </Information>
        </Grid>
        {!!(post.template.tags?.length && !isNewsfeed) && (
          <>
            <Spacer height="24px" />
            <Tags tags={post.template.tags} />
          </>
        )}
      </Post>

      {props.stickyUseDesignButton && (
        <MobileEditTemplateButtonWrapper>
          <EditTemplateButton />
        </MobileEditTemplateButtonWrapper>
      )}
      <VerifiedModal
        isOpen={showVerified}
        onClose={() => setShowVerified(false)}
      />
    </>
  );
};

export default withEventScope(DesignPost, { module: 'designPost' });
